import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import TracksList from "@/pages/TracksList";
import TrackEditor from "@/pages/TrackEditor";
import ReadyTracks from "@/pages/ReadyTracks";
import LicenseRequestsList from "@/pages/LicenseRequestsList";
import LicenseRequestEditor from "@/pages/LicenseRequestEditor";
import SubmissionsList from "@/pages/SubmissionsList";
import SubmissionEditor from "@/pages/SubmissionEditor";
import AuditLog from "@/pages/AuditLog";
import Settings from "@/pages/Settings";
import DocumentsList from "@/pages/DocumentsList";
import DocumentVault from "@/pages/DocumentVault";
import DocumentEditor from "@/pages/DocumentEditor";
import ComplianceReview from "@/pages/ComplianceReview";
import AIReviewNotes from "@/pages/AIReviewNotes";
import PRODirectory from "@/pages/PRODirectory";
import BetaOperations from "@/pages/BetaOperations";
import ChangePassword from "@/pages/ChangePassword";
import MyProfile from "@/pages/MyProfile";
import CatalogCodeRegistry from "@/pages/CatalogCodeRegistry";
import BulkIntake from "@/pages/BulkIntake";
import SyncOpportunities from "@/pages/SyncOpportunities";
import SyncOpportunityDetail from "@/pages/SyncOpportunityDetail";
import ConsultationRequests from "@/pages/ConsultationRequests";
import ConsultationRequestDetail from "@/pages/ConsultationRequestDetail";
import Landing from "@/pages/Landing";
import D49 from "@/pages/D49";
import GFMP from "@/pages/GFMP";
import GFMPSyncCatalogue from "@/pages/GFMPSyncCatalogue";
import GFMPSyncInquiry from "@/pages/GFMPSyncInquiry";
import Kandake from "@/pages/Kandake";
import KandakeSubmitDemo from "@/pages/KandakeSubmitDemo";
import Demos from "@/pages/Demos";
import DemoDetail from "@/pages/DemoDetail";
import AdminEmail from "@/pages/AdminEmail";
import BetaShell from "@/components/BetaShell";
import BetaCatalog from "@/pages/BetaCatalog";
import BetaTrackDetail from "@/pages/BetaTrackDetail";
import BetaFeedback from "@/pages/BetaFeedback";
import BetaMyFeedback from "@/pages/BetaMyFeedback";
import AdminBetaFeedback from "@/pages/AdminBetaFeedback";
import CommunicationsHub from "@/pages/CommunicationsHub";
import ProofLedger from "@/pages/ProofLedger";
import IntakeWorkspaceEditor from "@/pages/IntakeWorkspaceEditor";
import IntakeChecklistPrint from "@/pages/IntakeChecklistPrint";
import TrackCompliancePage from "@/pages/TrackCompliancePage";
import SyncCommandDesk from "@/pages/SyncCommandDesk";
import SyncBriefEditor from "@/pages/SyncBriefEditor";
// MP12 Module 5 — Release Intelligence, Metadata Registry, Clean Sweep,
// Commercial Exception / Human Override.
import ReleaseIntelDesk from "@/pages/ReleaseIntelDesk";
import ReleaseIntelEditor from "@/pages/ReleaseIntelEditor";
import MarketSignalsDesk from "@/pages/MarketSignalsDesk";
import MetadataRegistryDesk from "@/pages/MetadataRegistryDesk";
import CleanSweepDesk from "@/pages/CleanSweepDesk";
import CommercialExceptionsDesk from "@/pages/CommercialExceptionsDesk";
// Kandake Iron Gate — AI Provenance & Eligibility (Advanced Features module).
import AIProvenance from "@/pages/AIProvenance";
// MP12 Module 6 — Security Separation, Restricted Identity Handling,
// Vault-Transfer Boundaries.
import RestrictedIdentityDesk from "@/pages/RestrictedIdentityDesk";
import RestrictedRecordsDesk from "@/pages/RestrictedRecordsDesk";
import VaultDocketDesk from "@/pages/VaultDocketDesk";
import BriefsDesk from "@/pages/BriefsDesk";
import BriefEditor from "@/pages/BriefEditor";
import CatalogIntelligence from "@/pages/CatalogIntelligence";
import NotFound from "@/pages/NotFound";

function Protected({ children, role }) {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user) return <Navigate to="/login" replace />;
  // MP10 — Industry Beta Reviewers must NEVER reach internal routes.
  // If one lands on a Protected (internal-shell) route, send them to /beta.
  if (user.role === "industry_beta_reviewer") return <Navigate to="/beta" replace />;
  if (role && user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

/**
 * RootRedirect — hostname-scoped default route for the "/" path.
 *
 * Only affects the "/" URL — every other route on every hostname is
 * unchanged, and every existing page continues to be reachable by direct
 * link from any hostname. This is a display-preference redirect, NOT a
 * security boundary (auth/permission gates are enforced by the backend).
 *
 * Mapping (case-insensitive on the trailing hostname, so vanity forms
 * like WWW.KANDAKECS.COM still resolve correctly):
 *   kandakecs.com                 → /kandake
 *   www.kandakecs.com             → /kandake
 *   admin.kandakecs.com           → /login
 *   everything else (Emergent     → <Landing />  (unchanged)
 *   preview + prod + localhost)
 */
function RootRedirect() {
  const host = (typeof window !== "undefined" && window.location && window.location.hostname)
    ? window.location.hostname.toLowerCase()
    : "";
  if (host === "kandakecs.com" || host === "www.kandakecs.com") {
    return <Navigate to="/kandake" replace />;
  }
  if (host === "admin.kandakecs.com") {
    return <Navigate to="/login" replace />;
  }
  return <Landing />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/d49" element={<D49 />} />
          <Route path="/gfmp" element={<GFMP />} />
          <Route path="/gfmp/sync-catalogue" element={<GFMPSyncCatalogue />} />
          <Route path="/gfmp/sync-catalogue/inquiry" element={<GFMPSyncInquiry />} />
          <Route path="/kandake" element={<Kandake />} />
          <Route path="/kandake/submit-demo" element={<KandakeSubmitDemo />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route element={<Protected><AppShell /></Protected>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/tracks" element={<TracksList />} />
            <Route path="/catalog-codes" element={<Protected role="super_admin"><CatalogCodeRegistry /></Protected>} />
            <Route path="/intake" element={<Protected role="super_admin"><BulkIntake /></Protected>} />
            <Route path="/sync-opportunities" element={<SyncOpportunities />} />
            <Route path="/sync-opportunities/:id" element={<SyncOpportunityDetail />} />
            <Route path="/consultation-requests" element={<ConsultationRequests />} />
            <Route path="/consultation-requests/:id" element={<ConsultationRequestDetail />} />
            <Route path="/demos" element={<Demos />} />
            <Route path="/demos/:id" element={<DemoDetail />} />
            <Route path="/admin/email" element={<Protected role="super_admin"><AdminEmail /></Protected>} />
            <Route path="/admin/beta-feedback" element={<Protected><AdminBetaFeedback /></Protected>} />
            <Route path="/tracks/new" element={<TrackEditor mode="new" />} />
            <Route path="/tracks/:id" element={<TrackEditor mode="edit" />} />
            <Route path="/tracks/:trackId/compliance" element={<TrackCompliancePage />} />
            <Route path="/ready-tracks" element={<ReadyTracks />} />
            <Route path="/license-requests" element={<LicenseRequestsList />} />
            <Route path="/license-requests/new" element={<LicenseRequestEditor mode="new" />} />
            <Route path="/license-requests/:id" element={<LicenseRequestEditor mode="edit" />} />
            <Route path="/submissions" element={<SubmissionsList />} />
            <Route path="/submissions/new" element={<SubmissionEditor mode="new" />} />
            <Route path="/submissions/:id" element={<SubmissionEditor mode="edit" />} />
            <Route path="/documents" element={<DocumentsList />} />
            <Route path="/vault" element={<DocumentVault />} />
            <Route path="/documents/new" element={<DocumentEditor mode="new" />} />
            <Route path="/documents/:id" element={<DocumentEditor mode="edit" />} />
            <Route path="/compliance" element={<ComplianceReview />} />
            <Route path="/ai-review-notes" element={<AIReviewNotes />} />
            <Route path="/pro-directory" element={<PRODirectory />} />
            <Route path="/admin/beta" element={<Protected role="super_admin"><BetaOperations /></Protected>} />
            <Route path="/audit" element={<AuditLog />} />
            <Route path="/account/security" element={<ChangePassword />} />
            <Route path="/account/profile" element={<MyProfile />} />
            <Route path="/me/profile" element={<MyProfile />} />
            <Route path="/settings" element={<Protected role="super_admin"><Settings /></Protected>} />
            {/* MP12 Advanced Features — Module 1: Communications Hub + Proof Ledger */}
            <Route path="/advanced/communications" element={<CommunicationsHub />} />
            <Route path="/advanced/proof-ledger" element={<ProofLedger />} />
            <Route path="/intake-workspaces/:id" element={<IntakeWorkspaceEditor />} />
            <Route path="/intake-workspaces/:id/checklist" element={<IntakeChecklistPrint />} />
            {/* MP12 Advanced Features — Module 4: Sync Command Desk + Controlled Submission */}
            <Route path="/advanced/sync-command-desk" element={<SyncCommandDesk />} />
            <Route path="/advanced/sync-command-desk/:id" element={<SyncBriefEditor />} />
            {/* MP12 Advanced Features — Module 5: Release Intelligence, Metadata Registry, Clean Sweep, Commercial Exceptions */}
            <Route path="/advanced/release-intel" element={<ReleaseIntelDesk />} />
            <Route path="/advanced/release-intel/:id" element={<ReleaseIntelEditor />} />
            <Route path="/advanced/market-signals" element={<MarketSignalsDesk />} />
            <Route path="/advanced/metadata-registry" element={<MetadataRegistryDesk />} />
            <Route path="/advanced/clean-sweep" element={<CleanSweepDesk />} />
            <Route path="/advanced/commercial-exceptions" element={<CommercialExceptionsDesk />} />
            <Route path="/advanced/ai-provenance" element={<AIProvenance />} />
            {/* MP12 Advanced Features — Module 6: Security Separation, Restricted Identity, Vault Dockets */}
            <Route path="/advanced/restricted-identity" element={<RestrictedIdentityDesk />} />
            <Route path="/advanced/restricted-records" element={<RestrictedRecordsDesk />} />
            <Route path="/advanced/vault-dockets" element={<VaultDocketDesk />} />
            {/* MAGI #1 — Brief Intelligence. Multi-role read access is
                enforced locally by both pages; write actions use canWrite. */}
            <Route path="/advanced/briefs" element={<BriefsDesk />} />
            <Route path="/advanced/briefs/:briefId" element={<BriefEditor />} />
            {/* MAGI #2 — Catalog Intelligence V1 (canonical profiles). */}
            <Route path="/advanced/catalog-intelligence" element={<CatalogIntelligence />} />
          </Route>
          {/* MP10 — Industry Beta Reviewer surface. BetaShell internally
              verifies the role and bounces non-beta users to /dashboard,
              and non-authenticated users to /login. */}
          <Route element={<BetaShell />}>
            <Route path="/beta" element={<BetaCatalog />} />
            <Route path="/beta/track/:id" element={<BetaTrackDetail />} />
            <Route path="/beta/feedback" element={<BetaFeedback />} />
            <Route path="/beta/my-feedback" element={<BetaMyFeedback />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors closeButton position="top-right" />
    </AuthProvider>
  );
}

export default App;
